import { useEffect, useRef, useState } from 'react';

// Elements that animate in on scroll without needing a data-reveal attribute.
// Pages can still opt in to a specific direction with data-reveal="left|right|scale|fade".
const AUTO_REVEAL_SELECTORS = [
  '.section-label',
  '.section-title',
  '.section-subtitle',
  '.section-header',
  '.process-rect',
  '.feature-item',
  '.features-image',
  '.stat-item',
  '.product-card',
  '.user-card',
  '.feedback-card',
  '.ranking-container',
  '.gallery-container',
  '.order-card',
  '.orders-header',
  '.cart-item-card',
  '.order-stat-card',
  '.order-review-item',
  '.achievement-item',
  '.cart-summary',
  '.info-item',
  '.privacy-policy section',
  '.section-card',
  '.tree-item',
  '.footer-section',
  '.recent-card',
].join(',');

const STAGGER_MS = 90;
const MAX_STAGGER_MS = 540;

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Wires up scroll-reveal for the whole app. Re-scans on route change and
 * whenever new DOM appears (e.g. products loaded from the API).
 */
export function useAutoReveal(routeKey) {
  useEffect(() => {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) return undefined;

    const root = document.documentElement;
    root.classList.add('motion-ready');

    const io = new IntersectionObserver(
      (entries) => {
        // Stagger siblings that enter the viewport in the same frame.
        const batchByParent = new Map();
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const parent = el.parentElement;
          const index = batchByParent.get(parent) || 0;
          batchByParent.set(parent, index + 1);
          const delay = Math.min(index * STAGGER_MS, MAX_STAGGER_MS);
          el.style.setProperty('--reveal-delay', `${delay}ms`);
          el.setAttribute('data-revealed', '');
          io.unobserve(el);
          // Hand the element back to its own transitions/animations once revealed.
          setTimeout(() => {
            el.removeAttribute('data-reveal');
            el.style.removeProperty('--reveal-delay');
          }, delay + 900);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    const scan = (scope) => {
      if (!scope || !scope.querySelectorAll) return;
      const nodes = [];
      if (scope.matches && (scope.matches(AUTO_REVEAL_SELECTORS) || scope.hasAttribute('data-reveal'))) {
        nodes.push(scope);
      }
      scope.querySelectorAll(`${AUTO_REVEAL_SELECTORS},[data-reveal]`).forEach((n) => nodes.push(n));
      nodes.forEach((el) => {
        if (el.hasAttribute('data-revealed') || el.closest('[data-no-reveal]')) return;
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', '');
        io.observe(el);
      });
    };

    scan(document.body);

    // Safety net: if the browser delays IntersectionObserver callbacks (e.g. a
    // throttled background tab), reveal whatever is on screen once scrolling settles.
    let settleTimer = 0;
    const revealVisible = () => {
      document.querySelectorAll('[data-reveal]:not([data-revealed])').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.setAttribute('data-revealed', '');
          io.unobserve(el);
          setTimeout(() => el.removeAttribute('data-reveal'), 900);
        }
      });
    };
    const onScroll = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(revealVisible, 250);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const initialTimer = setTimeout(revealVisible, 1200);

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && scan(n)));
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', onScroll);
      clearTimeout(settleTimer);
      clearTimeout(initialTimer);
    };
  }, [routeKey]);
}

/** Thin progress bar at the very top of the viewport. */
export function ScrollProgress() {
  const ref = useRef(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      el.style.transform = `scaleX(${progress})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return <div ref={ref} className="scroll-progress" aria-hidden="true" />;
}

/** True once the page has been scrolled past `offset` pixels. */
export function useScrolled(offset = 8) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > offset);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [offset]);
  return scrolled;
}

/**
 * Animated number that counts up when it scrolls into view and
 * re-animates from its current value whenever `value` changes.
 */
export function CountUp({ value = 0, duration = 1600, suffix = '', className }) {
  const ref = useRef(null);
  const shown = useRef(0);
  const [display, setDisplay] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const target = Number(value) || 0;
    if (prefersReducedMotion()) {
      shown.current = target;
      setDisplay(target);
      return undefined;
    }
    const from = shown.current;
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      const current = Math.round(from + (target - from) * eased);
      shown.current = current;
      setDisplay(current);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, visible, duration]);

  return (
    <span ref={ref} className={className}>
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}
